(() => {
  // ../../packages/utils/src/index.ts
  var tryCatch = async (promiseOrFn, options) => {
    try {
      const data = await (typeof promiseOrFn === "function"
        ? promiseOrFn()
        : promiseOrFn);
      try {
        options?.onSuccess?.(data);
      } catch {}
      return [data, null];
    } catch (error) {
      try {
        options?.onError?.(error);
      } catch {}
      return [null, error];
    } finally {
      try {
        options?.onSettled?.();
      } catch {}
    }
  };

  // ../../node_modules/destr/dist/index.mjs
  var suspectProtoRx =
    /"(?:_|\\u0{2}5[Ff]){2}(?:p|\\u0{2}70)(?:r|\\u0{2}72)(?:o|\\u0{2}6[Ff])(?:t|\\u0{2}74)(?:o|\\u0{2}6[Ff])(?:_|\\u0{2}5[Ff]){2}"\s*:/;
  var suspectConstructorRx =
    /"(?:c|\\u0063)(?:o|\\u006[Ff])(?:n|\\u006[Ee])(?:s|\\u0073)(?:t|\\u0074)(?:r|\\u0072)(?:u|\\u0075)(?:c|\\u0063)(?:t|\\u0074)(?:o|\\u006[Ff])(?:r|\\u0072)"\s*:/;
  var JsonSigRx = /^\s*["[{]|^\s*-?\d{1,16}(\.\d{1,17})?([Ee][+-]?\d+)?\s*$/;
  function jsonParseTransform(key, value) {
    if (
      key === "__proto__" ||
      (key === "constructor" &&
        value &&
        typeof value === "object" &&
        "prototype" in value)
    ) {
      warnKeyDropped(key);
      return;
    }
    return value;
  }
  function warnKeyDropped(key) {
    console.warn(
      `[destr] Dropping "${key}" key to prevent prototype pollution.`,
    );
  }
  function destr(value, options = {}) {
    if (typeof value !== "string") {
      return value;
    }
    if (
      value[0] === '"' &&
      value[value.length - 1] === '"' &&
      value.indexOf("\\") === -1
    ) {
      return value.slice(1, -1);
    }
    const _value = value.trim();
    if (_value.length <= 9) {
      switch (_value.toLowerCase()) {
        case "true": {
          return true;
        }
        case "false": {
          return false;
        }
        case "undefined": {
          return;
        }
        case "null": {
          return null;
        }
        case "nan": {
          return Number.NaN;
        }
        case "infinity": {
          return Number.POSITIVE_INFINITY;
        }
        case "-infinity": {
          return Number.NEGATIVE_INFINITY;
        }
      }
    }
    if (!JsonSigRx.test(value)) {
      if (options.strict) {
        throw new SyntaxError("[destr] Invalid JSON");
      }
      return value;
    }
    try {
      if (suspectProtoRx.test(value) || suspectConstructorRx.test(value)) {
        if (options.strict) {
          throw new Error("[destr] Possible prototype pollution");
        }
        return JSON.parse(value, jsonParseTransform);
      }
      return JSON.parse(value);
    } catch (error) {
      if (options.strict) {
        throw error;
      }
      return value;
    }
  }

  // ../../node_modules/ufo/dist/index.mjs
  var r = String.fromCharCode;
  var HASH_RE = /#/g;
  var AMPERSAND_RE = /&/g;
  var SLASH_RE = /\//g;
  var EQUAL_RE = /=/g;
  var PLUS_RE = /\+/g;
  var ENC_CARET_RE = /%5e/gi;
  var ENC_BACKTICK_RE = /%60/gi;
  var ENC_PIPE_RE = /%7c/gi;
  var ENC_SPACE_RE = /%20/gi;
  function encode(text) {
    return encodeURI("" + text).replace(ENC_PIPE_RE, "|");
  }
  function encodeQueryValue(input) {
    return encode(typeof input === "string" ? input : JSON.stringify(input))
      .replace(PLUS_RE, "%2B")
      .replace(ENC_SPACE_RE, "+")
      .replace(HASH_RE, "%23")
      .replace(AMPERSAND_RE, "%26")
      .replace(ENC_BACKTICK_RE, "`")
      .replace(ENC_CARET_RE, "^")
      .replace(SLASH_RE, "%2F");
  }
  function encodeQueryKey(text) {
    return encodeQueryValue(text).replace(EQUAL_RE, "%3D");
  }
  function decode(text = "") {
    try {
      return decodeURIComponent("" + text);
    } catch {
      return "" + text;
    }
  }
  function decodeQueryKey(text) {
    return decode(text.replace(PLUS_RE, " "));
  }
  function decodeQueryValue(text) {
    return decode(text.replace(PLUS_RE, " "));
  }
  function parseQuery(parametersString = "") {
    const object = /* @__PURE__ */ Object.create(null);
    if (parametersString[0] === "?") {
      parametersString = parametersString.slice(1);
    }
    for (const parameter of parametersString.split("&")) {
      const s = parameter.match(/([^=]+)=?(.*)/) || [];
      if (s.length < 2) {
        continue;
      }
      const key = decodeQueryKey(s[1]);
      if (key === "__proto__" || key === "constructor") {
        continue;
      }
      const value = decodeQueryValue(s[2] || "");
      if (object[key] === undefined) {
        object[key] = value;
      } else if (Array.isArray(object[key])) {
        object[key].push(value);
      } else {
        object[key] = [object[key], value];
      }
    }
    return object;
  }
  function encodeQueryItem(key, value) {
    if (typeof value === "number" || typeof value === "boolean") {
      value = String(value);
    }
    if (!value) {
      return encodeQueryKey(key);
    }
    if (Array.isArray(value)) {
      return value
        .map((_value) => `${encodeQueryKey(key)}=${encodeQueryValue(_value)}`)
        .join("&");
    }
    return `${encodeQueryKey(key)}=${encodeQueryValue(value)}`;
  }
  function stringifyQuery(query) {
    return Object.keys(query)
      .filter((k) => query[k] !== undefined)
      .map((k) => encodeQueryItem(k, query[k]))
      .filter(Boolean)
      .join("&");
  }
  var PROTOCOL_STRICT_REGEX = /^[\s\w\0+.-]{2,}:([/\\]{1,2})/;
  var PROTOCOL_REGEX = /^[\s\w\0+.-]{2,}:([/\\]{2})?/;
  var PROTOCOL_RELATIVE_REGEX = /^([/\\]\s*){2,}[^/\\]/;
  var TRAILING_SLASH_RE = /\/$|\/\?|\/#/;
  var JOIN_LEADING_SLASH_RE = /^\.?\//;
  function hasProtocol(inputString, opts = {}) {
    if (typeof opts === "boolean") {
      opts = { acceptRelative: opts };
    }
    if (opts.strict) {
      return PROTOCOL_STRICT_REGEX.test(inputString);
    }
    return (
      PROTOCOL_REGEX.test(inputString) ||
      (opts.acceptRelative ? PROTOCOL_RELATIVE_REGEX.test(inputString) : false)
    );
  }
  function hasTrailingSlash(input = "", respectQueryAndFragment) {
    if (!respectQueryAndFragment) {
      return input.endsWith("/");
    }
    return TRAILING_SLASH_RE.test(input);
  }
  function withoutTrailingSlash(input = "", respectQueryAndFragment) {
    if (!respectQueryAndFragment) {
      return (hasTrailingSlash(input) ? input.slice(0, -1) : input) || "/";
    }
    if (!hasTrailingSlash(input, true)) {
      return input || "/";
    }
    let path = input;
    let fragment = "";
    const fragmentIndex = input.indexOf("#");
    if (fragmentIndex !== -1) {
      path = input.slice(0, fragmentIndex);
      fragment = input.slice(fragmentIndex);
    }
    const [s0, ...s] = path.split("?");
    const cleanPath = s0.endsWith("/") ? s0.slice(0, -1) : s0;
    return (
      (cleanPath || "/") + (s.length > 0 ? `?${s.join("?")}` : "") + fragment
    );
  }
  function withTrailingSlash(input = "", respectQueryAndFragment) {
    if (!respectQueryAndFragment) {
      return input.endsWith("/") ? input : input + "/";
    }
    if (hasTrailingSlash(input, true)) {
      return input || "/";
    }
    let path = input;
    let fragment = "";
    const fragmentIndex = input.indexOf("#");
    if (fragmentIndex !== -1) {
      path = input.slice(0, fragmentIndex);
      fragment = input.slice(fragmentIndex);
      if (!path) {
        return fragment;
      }
    }
    const [s0, ...s] = path.split("?");
    return s0 + "/" + (s.length > 0 ? `?${s.join("?")}` : "") + fragment;
  }
  function withBase(input, base) {
    if (isEmptyURL(base) || hasProtocol(input)) {
      return input;
    }
    const _base = withoutTrailingSlash(base);
    if (input.startsWith(_base)) {
      const nextChar = input[_base.length];
      if (!nextChar || nextChar === "/" || nextChar === "?") {
        return input;
      }
    }
    return joinURL(_base, input);
  }
  function withQuery(input, query) {
    const parsed = parseURL(input);
    const mergedQuery = { ...parseQuery(parsed.search), ...query };
    parsed.search = stringifyQuery(mergedQuery);
    return stringifyParsedURL(parsed);
  }
  function isEmptyURL(url) {
    return !url || url === "/";
  }
  function isNonEmptyURL(url) {
    return url && url !== "/";
  }
  function joinURL(base, ...input) {
    let url = base || "";
    for (const segment of input.filter((url2) => isNonEmptyURL(url2))) {
      if (url) {
        const _segment = segment.replace(JOIN_LEADING_SLASH_RE, "");
        url = withTrailingSlash(url) + _segment;
      } else {
        url = segment;
      }
    }
    return url;
  }
  var protocolRelative = Symbol.for("ufo:protocolRelative");
  function parseURL(input = "", defaultProto) {
    const _specialProtoMatch = input.match(
      /^[\s\0]*(blob:|data:|javascript:|vbscript:)(.*)/i,
    );
    if (_specialProtoMatch) {
      const [, _proto, _pathname = ""] = _specialProtoMatch;
      return {
        protocol: _proto.toLowerCase(),
        pathname: _pathname,
        href: _proto + _pathname,
        auth: "",
        host: "",
        search: "",
        hash: "",
      };
    }
    if (!hasProtocol(input, { acceptRelative: true })) {
      return defaultProto ? parseURL(defaultProto + input) : parsePath(input);
    }
    const [, protocol = "", auth, hostAndPath = ""] =
      input
        .replace(/\\/g, "/")
        .match(/^[\s\0]*([\w+.-]{2,}:)?\/\/([^/@]+@)?(.*)/) || [];
    let [, host = "", path = ""] = hostAndPath.match(/([^#/?]*)(.*)?/) || [];
    if (protocol === "file:") {
      path = path.replace(/\/(?=[A-Za-z]:)/, "");
    }
    const { pathname, search, hash } = parsePath(path);
    return {
      protocol: protocol.toLowerCase(),
      auth: auth ? auth.slice(0, Math.max(0, auth.length - 1)) : "",
      host,
      pathname,
      search,
      hash,
      [protocolRelative]: !protocol,
    };
  }
  function parsePath(input = "") {
    const [pathname = "", search = "", hash = ""] = (
      input.match(/([^#?]*)(\?[^#]*)?(#.*)?/) || []
    ).splice(1);
    return {
      pathname,
      search,
      hash,
    };
  }
  function stringifyParsedURL(parsed) {
    const pathname = parsed.pathname || "";
    const search = parsed.search
      ? (parsed.search.startsWith("?") ? "" : "?") + parsed.search
      : "";
    const hash = parsed.hash || "";
    const auth = parsed.auth ? parsed.auth + "@" : "";
    const host = parsed.host || "";
    const proto =
      parsed.protocol || parsed[protocolRelative]
        ? (parsed.protocol || "") + "//"
        : "";
    return proto + auth + host + pathname + search + hash;
  }

  // ../../node_modules/ofetch/dist/shared/ofetch.CWycOUEr.mjs
  class FetchError extends Error {
    constructor(message, opts) {
      super(message, opts);
      this.name = "FetchError";
      if (opts?.cause && !this.cause) {
        this.cause = opts.cause;
      }
    }
  }
  function createFetchError(ctx) {
    const errorMessage = ctx.error?.message || ctx.error?.toString() || "";
    const method = ctx.request?.method || ctx.options?.method || "GET";
    const url = ctx.request?.url || String(ctx.request) || "/";
    const requestStr = `[${method}] ${JSON.stringify(url)}`;
    const statusStr = ctx.response
      ? `${ctx.response.status} ${ctx.response.statusText}`
      : "<no response>";
    const message = `${requestStr}: ${statusStr}${errorMessage ? ` ${errorMessage}` : ""}`;
    const fetchError = new FetchError(
      message,
      ctx.error ? { cause: ctx.error } : undefined,
    );
    for (const key of ["request", "options", "response"]) {
      Object.defineProperty(fetchError, key, {
        get() {
          return ctx[key];
        },
      });
    }
    for (const [key, refKey] of [
      ["data", "_data"],
      ["status", "status"],
      ["statusCode", "status"],
      ["statusText", "statusText"],
      ["statusMessage", "statusText"],
    ]) {
      Object.defineProperty(fetchError, key, {
        get() {
          return ctx.response && ctx.response[refKey];
        },
      });
    }
    return fetchError;
  }
  var payloadMethods = new Set(
    Object.freeze(["PATCH", "POST", "PUT", "DELETE"]),
  );
  function isPayloadMethod(method = "GET") {
    return payloadMethods.has(method.toUpperCase());
  }
  function isJSONSerializable(value) {
    if (value === undefined) {
      return false;
    }
    const t = typeof value;
    if (t === "string" || t === "number" || t === "boolean" || t === null) {
      return true;
    }
    if (t !== "object") {
      return false;
    }
    if (Array.isArray(value)) {
      return true;
    }
    if (value.buffer) {
      return false;
    }
    if (value instanceof FormData || value instanceof URLSearchParams) {
      return false;
    }
    return (
      (value.constructor && value.constructor.name === "Object") ||
      typeof value.toJSON === "function"
    );
  }
  var textTypes = /* @__PURE__ */ new Set([
    "image/svg",
    "application/xml",
    "application/xhtml",
    "application/html",
  ]);
  var JSON_RE = /^application\/(?:[\w!#$%&*.^`~-]*\+)?json(;.+)?$/i;
  function detectResponseType(_contentType = "") {
    if (!_contentType) {
      return "json";
    }
    const contentType = _contentType.split(";").shift() || "";
    if (JSON_RE.test(contentType)) {
      return "json";
    }
    if (contentType === "text/event-stream") {
      return "stream";
    }
    if (textTypes.has(contentType) || contentType.startsWith("text/")) {
      return "text";
    }
    return "blob";
  }
  function resolveFetchOptions(request, input, defaults, Headers) {
    const headers = mergeHeaders(
      input?.headers ?? request?.headers,
      defaults?.headers,
      Headers,
    );
    let query;
    if (defaults?.query || defaults?.params || input?.params || input?.query) {
      query = {
        ...defaults?.params,
        ...defaults?.query,
        ...input?.params,
        ...input?.query,
      };
    }
    return {
      ...defaults,
      ...input,
      query,
      params: query,
      headers,
    };
  }
  function mergeHeaders(input, defaults, Headers) {
    if (!defaults) {
      return new Headers(input);
    }
    const headers = new Headers(defaults);
    if (input) {
      for (const [key, value] of Symbol.iterator in input ||
      Array.isArray(input)
        ? input
        : new Headers(input)) {
        headers.set(key, value);
      }
    }
    return headers;
  }
  async function callHooks(context, hooks) {
    if (hooks) {
      if (Array.isArray(hooks)) {
        for (const hook of hooks) {
          await hook(context);
        }
      } else {
        await hooks(context);
      }
    }
  }
  var retryStatusCodes = /* @__PURE__ */ new Set([
    408, 409, 425, 429, 500, 502, 503, 504,
  ]);
  var nullBodyResponses = /* @__PURE__ */ new Set([101, 204, 205, 304]);
  function createFetch(globalOptions = {}) {
    const {
      fetch = globalThis.fetch,
      Headers = globalThis.Headers,
      AbortController = globalThis.AbortController,
    } = globalOptions;
    async function onError(context) {
      const isAbort =
        (context.error &&
          context.error.name === "AbortError" &&
          !context.options.timeout) ||
        false;
      if (context.options.retry !== false && !isAbort) {
        let retries;
        if (typeof context.options.retry === "number") {
          retries = context.options.retry;
        } else {
          retries = isPayloadMethod(context.options.method) ? 0 : 1;
        }
        const responseCode =
          (context.response && context.response.status) || 500;
        if (
          retries > 0 &&
          (Array.isArray(context.options.retryStatusCodes)
            ? context.options.retryStatusCodes.includes(responseCode)
            : retryStatusCodes.has(responseCode))
        ) {
          const retryDelay =
            typeof context.options.retryDelay === "function"
              ? context.options.retryDelay(context)
              : context.options.retryDelay || 0;
          if (retryDelay > 0) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
          return $fetchRaw(context.request, {
            ...context.options,
            retry: retries - 1,
          });
        }
      }
      const error = createFetchError(context);
      if (Error.captureStackTrace) {
        Error.captureStackTrace(error, $fetchRaw);
      }
      throw error;
    }
    const $fetchRaw = async function $fetchRaw2(_request, _options = {}) {
      const context = {
        request: _request,
        options: resolveFetchOptions(
          _request,
          _options,
          globalOptions.defaults,
          Headers,
        ),
        response: undefined,
        error: undefined,
      };
      if (context.options.method) {
        context.options.method = context.options.method.toUpperCase();
      }
      if (context.options.onRequest) {
        await callHooks(context, context.options.onRequest);
        if (!(context.options.headers instanceof Headers)) {
          context.options.headers = new Headers(context.options.headers || {});
        }
      }
      if (typeof context.request === "string") {
        if (context.options.baseURL) {
          context.request = withBase(context.request, context.options.baseURL);
        }
        if (context.options.query) {
          context.request = withQuery(context.request, context.options.query);
          delete context.options.query;
        }
        if ("query" in context.options) {
          delete context.options.query;
        }
        if ("params" in context.options) {
          delete context.options.params;
        }
      }
      if (context.options.body && isPayloadMethod(context.options.method)) {
        if (isJSONSerializable(context.options.body)) {
          const contentType = context.options.headers.get("content-type");
          if (typeof context.options.body !== "string") {
            context.options.body =
              contentType === "application/x-www-form-urlencoded"
                ? new URLSearchParams(context.options.body).toString()
                : JSON.stringify(context.options.body);
          }
          if (!contentType) {
            context.options.headers.set("content-type", "application/json");
          }
          if (!context.options.headers.has("accept")) {
            context.options.headers.set("accept", "application/json");
          }
        } else if (
          ("pipeTo" in context.options.body &&
            typeof context.options.body.pipeTo === "function") ||
          typeof context.options.body.pipe === "function"
        ) {
          if (!("duplex" in context.options)) {
            context.options.duplex = "half";
          }
        }
      }
      let abortTimeout;
      if (!context.options.signal && context.options.timeout) {
        const controller = new AbortController();
        abortTimeout = setTimeout(() => {
          const error = new Error(
            "[TimeoutError]: The operation was aborted due to timeout",
          );
          error.name = "TimeoutError";
          error.code = 23;
          controller.abort(error);
        }, context.options.timeout);
        context.options.signal = controller.signal;
      }
      try {
        context.response = await fetch(context.request, context.options);
      } catch (error) {
        context.error = error;
        if (context.options.onRequestError) {
          await callHooks(context, context.options.onRequestError);
        }
        return await onError(context);
      } finally {
        if (abortTimeout) {
          clearTimeout(abortTimeout);
        }
      }
      const hasBody =
        (context.response.body || context.response._bodyInit) &&
        !nullBodyResponses.has(context.response.status) &&
        context.options.method !== "HEAD";
      if (hasBody) {
        const responseType =
          (context.options.parseResponse
            ? "json"
            : context.options.responseType) ||
          detectResponseType(
            context.response.headers.get("content-type") || "",
          );
        switch (responseType) {
          case "json": {
            const data = await context.response.text();
            const parseFunction = context.options.parseResponse || destr;
            context.response._data = parseFunction(data);
            break;
          }
          case "stream": {
            context.response._data =
              context.response.body || context.response._bodyInit;
            break;
          }
          default: {
            context.response._data = await context.response[responseType]();
          }
        }
      }
      if (context.options.onResponse) {
        await callHooks(context, context.options.onResponse);
      }
      if (
        !context.options.ignoreResponseError &&
        context.response.status >= 400 &&
        context.response.status < 600
      ) {
        if (context.options.onResponseError) {
          await callHooks(context, context.options.onResponseError);
        }
        return await onError(context);
      }
      return context.response;
    };
    const $fetch = async function $fetch2(request, options) {
      const r2 = await $fetchRaw(request, options);
      return r2._data;
    };
    $fetch.raw = $fetchRaw;
    $fetch.native = (...args) => fetch(...args);
    $fetch.create = (defaultOptions = {}, customGlobalOptions = {}) =>
      createFetch({
        ...globalOptions,
        ...customGlobalOptions,
        defaults: {
          ...globalOptions.defaults,
          ...customGlobalOptions.defaults,
          ...defaultOptions,
        },
      });
    return $fetch;
  }

  // ../../node_modules/ofetch/dist/index.mjs
  var _globalThis = (function () {
    if (typeof globalThis !== "undefined") {
      return globalThis;
    }
    if (typeof self !== "undefined") {
      return self;
    }
    if (typeof window !== "undefined") {
      return window;
    }
    if (typeof global !== "undefined") {
      return global;
    }
    throw new Error("unable to locate global object");
  })();
  var fetch = _globalThis.fetch
    ? (...args) => _globalThis.fetch(...args)
    : () =>
        Promise.reject(new Error("[ofetch] global.fetch is not supported!"));
  var Headers = _globalThis.Headers;
  var AbortController = _globalThis.AbortController;
  var ofetch = createFetch({ fetch, Headers, AbortController });

  // assets/scripts/header/header-drawer-search.ts
  class HeaderDrawerSearch {
    searchInput;
    resultsContainer;
    searchTimeout;
    constructor() {
      this.searchInput = null;
      this.resultsContainer = null;
      this.searchTimeout = null;
      this.init();
    }
    init() {
      this.searchInput = document.getElementById("MobileMenuSearchInput");
      this.resultsContainer = document.querySelector(
        ".menu-drawer__search-results",
      );
      if (!this.searchInput) return;
      const searchForm = this.searchInput.closest("form");
      if (searchForm) {
        searchForm.addEventListener("submit", (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        });
      }
      this.searchInput.addEventListener("input", (e) => {
        this.handleSearch(e);
        this.updateResetButton();
      });
      const resetButton = document.querySelector(
        ".menu-drawer__search-section .search-hero__clear-btn",
      );
      if (resetButton) {
        resetButton.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (this.searchInput) {
              this.searchInput.value = "";
              this.searchInput.focus();
            }
            this.hideResults();
            this.updateResetButton();
          },
          true,
        );
      }
    }
    updateResetButton() {
      const resetButton = document.querySelector(
        ".menu-drawer__search-section .search-hero__clear-btn",
      );
      if (resetButton && this.searchInput) {
        if (this.searchInput.value.length > 0) {
          resetButton.classList.remove("hidden");
        } else {
          resetButton.classList.add("hidden");
        }
      }
    }
    async handleSearch(e) {
      const input = e.target;
      const query = input.value.trim();
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      if (query.length === 0) {
        this.hideResults();
        return;
      }
      this.searchTimeout = window.setTimeout(async () => {
        await this.performSearch(query);
      }, 100);
    }
    async performSearch(query) {
      const [data, error] = await tryCatch(
        ofetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article&resources[limit]=8&resources[options][unavailable_products]=show`,
        ),
      );
      if (!this.searchInput || this.searchInput.value.trim().length === 0) {
        this.hideResults();
        return;
      }
      if (error) {
        console.error("Search error:", error);
        return;
      }
      const results = data?.resources?.results;
      const productHandles = (results?.products ?? [])
        .map((p) => p.handle)
        .slice(0, 4);
      const articleUrls = (results?.articles ?? [])
        .map((a) => a.url)
        .slice(0, 3);
      if (productHandles.length === 0 && articleUrls.length === 0) {
        this.renderEmpty();
        return;
      }
      const productRequests = productHandles.map((handle) =>
        ofetch(`/products/${handle}?view=search-list-item`, {
          responseType: "text",
        }).catch(() => null),
      );
      const articleRequests = articleUrls.map((url) => {
        const path = new URL(url, window.location.origin).pathname;
        return ofetch(`${path}?view=search-list-item`, {
          responseType: "text",
        }).catch(() => null);
      });
      const [productHtmls, articleHtmls] = await Promise.all([
        Promise.all(productRequests),
        Promise.all(articleRequests),
      ]);
      if (!this.searchInput || this.searchInput.value.trim().length === 0) {
        this.hideResults();
        return;
      }
      const validProductHtmls = productHtmls.filter((html) => html !== null);
      const validArticleHtmls = articleHtmls.filter((html) => html !== null);
      if (validProductHtmls.length === 0 && validArticleHtmls.length === 0) {
        this.renderEmpty();
        return;
      }
      this.renderResults(validProductHtmls, validArticleHtmls);
    }
    extractItems(htmlCards) {
      const cleanedItems = [];
      for (const html of htmlCards) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const liElement = tempDiv.querySelector("li.product-list-item");
        if (liElement) {
          cleanedItems.push(liElement.outerHTML);
        }
      }
      return cleanedItems;
    }
    buildSection(title, items) {
      if (items.length === 0) return "";
      return `
			<div class="menu-drawer__search-results-content">
				<h3 class="page-width">${title}</h3>
				<ul class="product-list" role="list">
					${items.join("")}
				</ul>
			</div>
		`;
    }
    renderResults(productHtmls, articleHtmls) {
      if (!this.resultsContainer) {
        this.hideResults();
        return;
      }
      const productItems = this.extractItems(productHtmls);
      const articleItems = this.extractItems(articleHtmls);
      if (productItems.length === 0 && articleItems.length === 0) {
        this.hideResults();
        return;
      }
      const query = this.searchInput?.value ?? "";
      const resultsHtml = `
			${this.buildSection("Products", productItems)}
			${this.buildSection("Articles", articleItems)}
			<a class="view-all" href="/search?q=${encodeURIComponent(query)}">View all results for "${query}" →</a>
		`;
      this.resultsContainer.innerHTML = resultsHtml;
      this.resultsContainer.style.display = "block";
      this.hideMenuItems();
    }
    renderEmpty() {
      if (!this.resultsContainer) return;
      const query = this.searchInput?.value.trim() ?? "";
      this.resultsContainer.innerHTML = `
			<div class="search-empty">
				<p class="search-empty__title">No results found for &ldquo;${query}&rdquo;</p>
				<p class="search-empty__text">
					Check your spelling or try a different search.
				</p>
				<a class="search-empty__link" href="/collections/shop-all">Shop all &rarr;</a>
			</div>
		`;
      this.resultsContainer.style.display = "block";
      this.hideMenuItems();
    }
    hideMenuItems() {
      const menuList = document.querySelector(".header-drawer-links-wrapper");
      const accountLink = document.querySelector(".menu-drawer__account-link");
      const utilityLinks = document.querySelector(
        ".menu-drawer__utility-links",
      );
      if (menuList) menuList.style.display = "none";
      if (utilityLinks) utilityLinks.style.display = "none";
      if (accountLink) accountLink.style.display = "none";
    }
    hideResults() {
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = "";
        this.resultsContainer.style.display = "none";
      }
      const menuList = document.querySelector(".header-drawer-links-wrapper");
      const utilityLinks = document.querySelector(
        ".menu-drawer__utility-links",
      );
      if (menuList) menuList.style.display = "";
      if (utilityLinks) utilityLinks.style.display = "";
    }
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      new HeaderDrawerSearch();
    });
  } else {
    new HeaderDrawerSearch();
  }
})();
